import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { IColumnsMap } from 'libs/design-system/src/types/data_table';
import { DataTable } from '@unified-checkout/design-system';
import { Download, AnimatedSpinner } from '@unified-checkout/design-system/icons/index';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useLangStore } from '@app/dashboard/store/lang';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useIntegrationsStore } from '@app/dashboard/store/integrations';
import { getTerminalStatistics, getTerminalExports } from '@app/dashboard/services/inner_layout/summary';
import { getCountryCurrency } from '@app/dashboard/utils/countriesUtils';
import { mainPreDefinedRanges } from '@app/dashboard/utils/dateRangePicker';
import { formatToISODateTimeUTC, formatDateRangeUTC } from '@app/dashboard/utils/dateFormats';
import { formatAmountWithCommas } from '@unified-checkout/design-system';
import { convertCentsToMajorUnit } from '@app/dashboard/utils/convertAmounts';
import { Metrics } from '@app/dashboard/components/inner_layout/Metrics';
import { Header, ISummaryFilters } from './Header';
import styles from './summary.module.scss';

const TABLE_ID = 'summary_table';

export const Summary = () => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const { isLive } = useTestLiveModeStore();
  const { currencies } = useIntegrationsStore();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const [isError, setIsError] = useState(false);
  const [dateRange, setDateRange] = useState<ISummaryFilters>(() => {
    const defaultPreDefinedRange = mainPreDefinedRanges(t)?.find((range) => range.default);
    if (defaultPreDefinedRange?.selectionRange[0] && defaultPreDefinedRange?.selectionRange[1]) {
      const [startDate, endDate] = defaultPreDefinedRange.selectionRange;
      return {
        start_time: formatToISODateTimeUTC(startDate, 'Z', 'start'),
        end_time: formatToISODateTimeUTC(endDate, 'Z', 'end'),
      };
    }
    return {};
  });
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const {
    data: statisticsData,
    isLoading: isStatisticsLoading,
    isError: isStatisticsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_TERMINAL_STATISTICS, dateRange],
    queryFn: () => getTerminalStatistics(dateRange),
    enabled: !!(dateRange.start_time && dateRange.end_time),
  });

  // Get date range description based on selected dates
  const dateRangeDescription = useMemo(() => {
    if (!dateRange.start_time || !dateRange.end_time) {
      return '';
    }

    try {
      const startDate = new Date(dateRange.start_time);
      const endDate = new Date(dateRange.end_time);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return '';
      }

      const isSameDay = 
        startDate.getUTCFullYear() === endDate.getUTCFullYear() &&
        startDate.getUTCMonth() === endDate.getUTCMonth() &&
        startDate.getUTCDate() === endDate.getUTCDate();

      if (isSameDay) {
        // Show single date if same day (e.g., "Feb 10, 2026")
        const year = startDate.getUTCFullYear();
        const currentYear = new Date().getUTCFullYear();
        const yearOption = year !== currentYear ? 'long' : undefined;
        return formatDateRangeUTC(startDate, yearOption);
      }

      // Show the actual date range format in UTC (e.g., "Feb 10 ~ Feb 16, 2026")
      const startYear = startDate.getUTCFullYear();
      const endYear = endDate.getUTCFullYear();
      const currentYear = new Date().getUTCFullYear();
      const yearOption = (startYear !== currentYear || endYear !== currentYear) ? 'long' : undefined;
      
      return `${formatDateRangeUTC(startDate, yearOption)} ~ ${formatDateRangeUTC(endDate, yearOption)}`;
    } catch (error) {
      return '';
    }
  }, [dateRange.start_time, dateRange.end_time, t]);

  const metricsData = useMemo(() => {
    const data = statisticsData?.data || {};
    const currency = currencies ? getCountryCurrency(isLive, currencies) : 'EGP';
    const totalAmountCents = data.total_amount_cents || 0;
    const totalAmount = convertCentsToMajorUnit(totalAmountCents, currency);
    
    return [
      {
        title: t('innerLayout.summaryPage.metrics.totalDevices'),
        value: data.total_devices || 0,
      },
      {
        title: t('innerLayout.summaryPage.metrics.totalRevenue'),
        value: formatAmountWithCommas({ 
          amount: totalAmount, 
          keepDecimals: true, 
          excludeZeroDecimals: true, 
          currency 
        }),
        currency: currency,
      },
      {
        title: t('innerLayout.summaryPage.metrics.totalTransactions'),
        value: data.total_count || 0,
      },
    ];
  }, [statisticsData, t, currencies, isLive]);

  const handleDownload = useCallback(async (row: {id: number, file: string}) => {
    if (!row.file) return;
    
    setDownloadingId(row.id);
    
    try {
      // Extract filename from URL
      const urlParts = row.file.split('/');
      const filename = urlParts[urlParts.length - 1] || 'export.csv';
      
      const response = await fetch(row.file);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback: try direct download attribute (works for same-origin or CORS-enabled)
      const link = document.createElement('a');
      link.href = row.file;
      link.setAttribute('download', row.file.split('/').pop() || 'export.csv');
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloadingId(null);
    }
  }, []);

const columnsMap: IColumnsMap = useMemo(() => {
  const currency = currencies ? getCountryCurrency(isLive, currencies) : 'EGP';

  return [
    {
      id: 'id',
      name: t('innerLayout.summaryPage.table.headers.id'),
      type: 'text',
      key: 'id',
    },
    {
      id: 'created_at',
      name: t('innerLayout.summaryPage.table.headers.createdAt'),
      type: 'date',
      key: 'created_at',
      hintText: 'created_at',
      format: (_: any, value: any) => {
        // Handle date format with space instead of 'T'
        if (typeof value === 'string' && value.includes(' ')) {
          return value.replace(' ', 'T');
        }
        return value;
      },
    },
    {
      id: 'terminal_id',
      name: t('innerLayout.summaryPage.table.headers.terminalId'),
      type: 'text',
      key: 'terminal.id',
    },
    {
      id: 'start_time',
      name: t('innerLayout.summaryPage.table.headers.startTime'),
      type: 'text',
      key: 'start_time',
    },
    {
      id: 'end_time',
      name: t('innerLayout.summaryPage.table.headers.endTime'),
      type: 'text',
      key: 'end_time',
    },
    {
      id: 'total_amount',
      name: t('innerLayout.summaryPage.table.headers.totalAmount'),
      type: 'amount_cents',
      key: 'transactions_total_amount_cents',
      format: (_: any, value: any) => {
        if (typeof value === 'string') {
          return Number(value);
        }
        return value;
      },
      additionalText: {
        directValue: currency,
        placement: lang === 'en' ? 'start' : 'end',
      },
    },
    {
      id: 'transactions_count',
      name: t('innerLayout.summaryPage.table.headers.transactionsCount'),
      type: 'text',
      key: 'transactions_count',
    },
    {
      id: 'action',
      name: t('innerLayout.summaryPage.table.headers.actions'),
      type: {
        conditionsType: 'direct_value',
        conditions: {
          action_btns: { file: /^https?:/ },
          badge: { file: '' },
        },
        default: 'badge',
      },
      key: {
        conditionsType: 'direct_value',
        conditions: {
          download: { file: /^https?:/ },
          pending: { file: '' },
        },
        default: 'pending',
      },
      label: {
        pending: {
          color: 'yellow',
          text: t('innerLayout.summaryPage.table.status.pending'),
        },
      },
      actionBtnsProps: (row: { id: number, file: string }) => [
        {
          leadingIcon: downloadingId === row.id ? AnimatedSpinner : Download,
          text: t('innerLayout.download'),
          onClickHandler: () => handleDownload(row),
          variant: 'link' as const,
          color: 'primary' as const,
          disabled: downloadingId !== null,
        },
      ],
    },
  ];
}, [t, lang, currencies, isLive, downloadingId, handleDownload]);

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_TERMINAL_EXPORTS, isLive, dateRange],
    [isLive, dateRange]
  );

  const queryFunctionParams = useMemo(
    () => ({
      ...dateRange,
      is_live: isLive,
    }),
    [dateRange, isLive]
  );

  const resetFiltersParams = useMemo(() => [isLive], [isLive]);

  return (
    <div className={styles['summary-wrapper']}>
      <Header
        filters={dateRange}
        onFiltersChange={setDateRange}
      />

      {dateRangeDescription && (
        <div className={styles['summary-description']}>
          {t('innerLayout.summaryPage.description', {
            dateRange: dateRangeDescription,
          })}
        </div>
      )}

      <div className={styles['summary-metrics']}>
        <Metrics
          data={metricsData}
          noOfCards={3}
          isError={isStatisticsError}
          isLoading={isStatisticsLoading}
        />
      </div>

      <DataTable
        lang={lang}
        tableBody={{
          lang: lang,
          tableId: TABLE_ID,
          dataLocation: 'data.results',
          columnsMap: columnsMap,
        }}
        pagination={{
          hideLabels: isMobile,
          type: 'cursor_based',
          dataLocation: {
            next: 'data.next',
            prev: 'data.previous',
          },
          isMobile: isMobile,
        }}
        queries={{
          queryKeys,
          queryFunction: getTerminalExports,
          queryFunctionParams,
          resetFiltersParams,
        }}
        errorState={{
          onError: (error) => setIsError(error),
        }}
        emptyState={{
          text: t('innerLayout.summaryPage.table.body.noData'),
          description: t(
            'innerLayout.summaryPage.table.body.noDataHint',
            'There are no terminal exports available for the selected date range.'
          ),
        }}
      />
    </div>
  );
};
